import { AccessLevel } from '@colonist/types';
import { isValueRequiredAccessLevel } from '@colonist/validation';
import { ClientUserState } from '../../../../shared/scripts/ClientUserState.ts';
import { UIUtilsEjsVariables } from '../../../../shared/scripts/UIUtilsEjsVariables.ts';
import { UIDeviceController } from '../../../../shared/scripts/ui_html/UIDeviceController.ts';
import { UICheckoutAjaxEndPoints } from '../../../../shared/scripts/ui_checkout/UICheckoutAjaxEndPoints.ts';
import { showSimpleDialog } from '../../../../shared/scripts/ui_popup/showSimpleDialog.ts';
import { clientI18n } from '../../../../shared/scripts/ClientI18n.ts';
import { StringUtils } from '@colonist/utils';
export class UIMDoubleTapPurchaseController {
    static getInstance() {
        if (this.instance == undefined) this.init();
        return this.instance;
    }
    static init() {
        if (!this.isDoubleTapPaymentEnabled()) return;
        this.instance = new UIMDoubleTapPurchaseController();
        void this.instance.warmUp();
    }
    warmUp() {
        void this.prepareNextPaymentRequest();
    }
    static isDoubleTapPaymentEnabledForCurrentPlatform() {
        if (UIDeviceController.isUserOnIOSMobileAppOrWeb()) return UIUtilsEjsVariables.shouldEnableIOSDoubleTapPaymentOnMobile;
        if (UIDeviceController.isUserOnAndroidMobileAppOrWeb()) return UIUtilsEjsVariables.shouldEnableAndroidDoubleTapPaymentOnMobile;
        return false;
    }
    static isDoubleTapPaymentEnabled() {
        var _ClientUserState_userState;
        if (typeof stripe == 'undefined') return false;
        if (!this.isDoubleTapPaymentEnabledForCurrentPlatform()) return false;
        if (UIUtilsEjsVariables.shouldEnableDoubleTapPaymentForExternalUsersOnMobile) return true;
        const doesUserHaveEnoughAccess = isValueRequiredAccessLevel((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.accessLevel, AccessLevel.Moderator);
        return doesUserHaveEnoughAccess;
    }
    static isCurrentPlatformPaymentMethodAvailable() {
        if (!this.isDoubleTapPaymentEnabled()) return false;
        return this.isDoubleTapPurchaseAvailable;
    }
    static async updatePaymentMethodAvailability(paymentRequest) {
        const availablePaymentMethods = await paymentRequest.canMakePayment();
        if (UIDeviceController.isUserOnIOSMobileAppOrWeb()) {
            this.isDoubleTapPurchaseAvailable = (availablePaymentMethods === null || availablePaymentMethods === void 0 ? void 0 : availablePaymentMethods.applePay) == true;
            return;
        }
        if (UIDeviceController.isUserOnAndroidMobileAppOrWeb()) {
            this.isDoubleTapPurchaseAvailable = (availablePaymentMethods === null || availablePaymentMethods === void 0 ? void 0 : availablePaymentMethods.googlePay) == true;
            return;
        }
        this.isDoubleTapPurchaseAvailable = false;
    }
    static createWarmupWalletPaymentRequest() {
        const colonistStripeCountry = 'US';
        const paymentCurrency = 'usd';
        const placeholderTotal = {
            label: 'Colonist',
            amount: 100
        };
        // Placeholder, only used for warmup, not real purchase
        return stripe.paymentRequest({
            country: colonistStripeCountry,
            currency: paymentCurrency,
            total: placeholderTotal,
            disableWallets: [
                'link',
                'browserCard'
            ]
        });
    }
    async prepareNextPaymentRequest() {
        if (!UIMDoubleTapPurchaseController.isDoubleTapPaymentEnabled()) return;
        try {
            const paymentRequest = UIMDoubleTapPurchaseController.createWarmupWalletPaymentRequest();
            await UIMDoubleTapPurchaseController.updatePaymentMethodAvailability(paymentRequest);
            if (!UIMDoubleTapPurchaseController.isCurrentPlatformPaymentMethodAvailable()) return;
            paymentRequest.on('paymentmethod', (paymentEvent)=>void this.handlePaymentMethod(paymentEvent));
            paymentRequest.on('cancel', ()=>{
                var _this_currentPurchaseRequest_onPurchaseCancel, _this_currentPurchaseRequest;
                (_this_currentPurchaseRequest = this.currentPurchaseRequest) === null || _this_currentPurchaseRequest === void 0 ? void 0 : (_this_currentPurchaseRequest_onPurchaseCancel = _this_currentPurchaseRequest.onPurchaseCancel) === null || _this_currentPurchaseRequest_onPurchaseCancel === void 0 ? void 0 : _this_currentPurchaseRequest_onPurchaseCancel.call(_this_currentPurchaseRequest);
                this.finishPurchase();
            });
            this.currentPaymentRequest = paymentRequest;
        } catch (error) {
            this.currentPaymentRequest = undefined;
        }
    }
    startPurchase(purchaseRequest) {
        if (this.currentPurchaseRequest != undefined) {
            purchaseRequest.onPurchaseFinished({});
            return;
        }
        const paymentRequest = this.currentPaymentRequest;
        this.currentPaymentRequest = undefined;
        this.currentPurchaseRequest = purchaseRequest;
        if (paymentRequest == undefined) {
            this.finishPurchase({
                shouldExecuteFallbackPurchase: true
            });
            return;
        }
        try {
            var _purchaseRequest_onPaymentSheetShown;
            paymentRequest.update({
                total: {
                    label: StringUtils.replaceHTMLCharacters(clientI18n.t(purchaseRequest.productName.key, purchaseRequest.productName.options), ' '),
                    amount: purchaseRequest.usdCentsAmount
                }
            });
            paymentRequest.show();
            (_purchaseRequest_onPaymentSheetShown = purchaseRequest.onPaymentSheetShown) === null || _purchaseRequest_onPaymentSheetShown === void 0 ? void 0 : _purchaseRequest_onPaymentSheetShown.call(purchaseRequest);
        } catch (error) {
            this.finishPurchase({
                shouldExecuteFallbackPurchase: true
            });
        }
    }
    async handlePaymentMethod(paymentEvent) {
        const purchaseRequest = this.currentPurchaseRequest;
        if (purchaseRequest == undefined) return;
        const clientSecret = await UIMDoubleTapPurchaseController.fetchClientSecret(purchaseRequest);
        if (clientSecret == undefined) {
            paymentEvent.complete('fail');
            this.finishPurchase({
                shouldExecuteFallbackPurchase: true
            });
            return;
        }
        await this.confirmWalletPayment(paymentEvent, clientSecret);
    }
    static async fetchClientSecret(purchaseRequest) {
        try {
            const { storeProductType, productType, itemQuantity, giftPurchaseData } = purchaseRequest;
            const response = await UICheckoutAjaxEndPoints.fetchStripePaymentClientSecret(storeProductType, productType, itemQuantity, giftPurchaseData);
            if ((response === null || response === void 0 ? void 0 : response.status) != true) return undefined;
            return response.clientSecret;
        } catch (error) {
            return undefined;
        }
    }
    static getTestModePaymentMethodId(walletPaymentMethodId) {
        const testCardMode = UIUtilsEjsVariables.mobileDoubleTapPaymentTestCardMode;
        if (testCardMode == 'normal') return walletPaymentMethodId;
        return testCardMode;
    }
    async confirmWalletPayment(paymentEvent, clientSecret) {
        var _confirmResult_paymentIntent;
        let paymentMethod = paymentEvent.paymentMethod.id;
        if (!UIUtilsEjsVariables.isProduction) {
            paymentMethod = UIMDoubleTapPurchaseController.getTestModePaymentMethodId(paymentMethod);
        }
        const confirmResult = await stripe.confirmCardPayment(clientSecret, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payment_method: paymentMethod
        }, {
            handleActions: false
        });
        if (confirmResult.error != undefined) {
            paymentEvent.complete('fail');
            this.finishPurchase();
            UIMDoubleTapPurchaseController.showPaymentFailedDialog(confirmResult.error.message);
            return;
        }
        paymentEvent.complete('success');
        const requiresAdditionalUserAction = ((_confirmResult_paymentIntent = confirmResult.paymentIntent) === null || _confirmResult_paymentIntent === void 0 ? void 0 : _confirmResult_paymentIntent.status) == 'requires_action';
        if (requiresAdditionalUserAction) {
            const actionResult = await stripe.confirmCardPayment(clientSecret);
            if (actionResult.error != undefined) {
                this.finishPurchase();
                UIMDoubleTapPurchaseController.showPaymentFailedDialog(actionResult.error.message);
                return;
            }
        }
        this.finishPurchase({
            isSuccessfulPurchase: true
        });
    }
    finishPurchase({ shouldExecuteFallbackPurchase = false, isSuccessfulPurchase = false } = {}) {
        const purchaseRequest = this.currentPurchaseRequest;
        if (purchaseRequest == undefined) return;
        this.currentPurchaseRequest = undefined;
        purchaseRequest.onPurchaseFinished({
            shouldExecuteFallbackPurchase: shouldExecuteFallbackPurchase,
            isSuccessfulPurchase: isSuccessfulPurchase
        });
        void this.prepareNextPaymentRequest();
    }
    static showPaymentFailedDialog(errorMessage) {
        const body = errorMessage ? {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: errorMessage
            }
        } : {
            key: 'strings:storePage.errors.failureTryAgainLater'
        };
        showSimpleDialog({
            title: {
                key: 'strings:storePage.errors.fail'
            },
            body: body
        });
    }
    constructor(){}
}
UIMDoubleTapPurchaseController.isDoubleTapPurchaseAvailable = false;
